<template>
  <b-card :title="lodash.startCase(cardTitle)">
    <template v-if="bankAccountItems.length > 0">
      <div class="border overflow-hidden rounded mb-2">
        <b-table
            :fields="bankAccountFields"
            :items="bankAccountItems"
            borderless
            class="m-0"
            hover
            responsive
            show-empty
        >
          <template #cell()="data">
            <template v-if="data.field.component">
              <component
                  :is="data.field.component"
                  v-bind="{ item: data.item }"
                  v-on="data.field['componentListeners'] || {}"
              ></component>
            </template>
            <template v-else>{{ data.value }}</template>
          </template>
          <template #empty="scope">
            <p class="my-auto mx-auto text-center text-muted">
              {{ noBankAccountTranslation }}
            </p>
          </template>
          <template #cell(bank.name)="data">
            <validation-provider
                v-slot="{ validated, errors }"
                :class="isModal ? 'col-md-12' : 'col-md-6 mb-3'"
                :name="lodash.upperFirst($t('customers.bank.bank_name'))"
                :vid="`bank_accounts.bank_id`"
                rules="required"
            >
              <b-form-group
                  :invalid-feedback="errors[0]"
                  :state="errors[0] ? false : validated ? true : null"
              >
                <VueSelectAjax
                    v-model="value[data.index].bank_id"
                    :params="{ sort: 'name,asc' }"
                    append-to-body
                    name="bank_accounts[bank_id]"
                    url="/banks"
                    @update:selected="handleBankSelected"
                ></VueSelectAjax>
              </b-form-group>
            </validation-provider>
          </template>
          <template #cell(account_number)="data">
            <validation-provider
                v-slot="{ validated, errors }"
                :name="lodash.upperFirst($t('customers.bank.account_number'))"
                :vid="`bank_accounts.account_number`"
                rules="required"
            >
              <b-form-group
                  :invalid-feedback="errors[0]"
                  :state="errors[0] ? false : validated ? true : null"
              >
                <b-form-input
                    v-model="value[data.index].account_number"
                    :state="errors[0] ? false : validated ? true : null"
                    type="number"
                />
              </b-form-group>
            </validation-provider>
          </template>
          <template #cell(name)="data">
            <validation-provider
                v-slot="{ validated, errors }"
                :name="lodash.upperFirst($t('customers.bank.account_name'))"
                :vid="`bank_accounts.account_name`"
                rules="required"
            >
              <b-form-group
                  :invalid-feedback="errors[0]"
                  :state="errors[0] ? false : validated ? true : null"
              >
                <b-form-input
                    id="account_name"
                    v-model="value[data.index].name"
                    :name="`bank_accounts[account_name]`"
                    :state="errors[0] ? false : validated ? true : null"
                    placeholder="Ex: John"
                    type="text"
                ></b-form-input>
              </b-form-group>
            </validation-provider>
          </template>
          <template #cell(action)="data">
            <div class="text-center" v-if="!data.item.id" @click="deleteBankAt(data.index)">
              <i class="fa fa-trash text-danger font-size-14" style="cursor:pointer;"></i>
            </div>
          </template>
        </b-table>
      </div>
      <b-button
          variant="white"
          class="button-add-bank"
          @click="addEmptyBankAccount"
      ><i class="fa fa-plus me-1"/>{{  lodash.startCase(addBankTranslation) }}</b-button>
    </template>
    <div v-else class="empty-state">
      <div class="empty-content">
        <p class="text-muted">{{ lodash.upperFirst(noBankAccountTranslation) }}</p>
        <b-button
            variant="outline-primary"
            @click="addEmptyBankAccount"
            class="mt-1"
        ><i class="fa fa-plus me-1"/>{{ lodash.startCase(addBankTranslation) }}
        </b-button>
      </div>
    </div>
  </b-card>
</template>

<script>
import VueSelectAjax from "@/components/inputs/vue-select-ajax";

export default {
  name: "CustomerBankAccounts",
  props: {
    value: {
      type: Array,
      required: true,
    },
    isModal: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    VueSelectAjax,
  },
  computed: {
    bankAccountItems() {
      return this.undeletedItems();
    },
    bankAccountFields() {
      return [
        {
          key: "bank.name",
          label: this.lodash.startCase(this.$t('bank_accounts.bank')),
          thStyle: {minWidth: '200px'},
        },
        {
          key: "account_number",
          label: this.lodash.startCase(this.$t('bank_accounts.account_number')),
        },
        {
          key: "name",
          label: this.lodash.startCase(this.$t('bank_accounts.name')),
        },
        {
          key: "action",
          label: "",
          tdClass: 'align-middle',
        },
      ];
    },
    cardTitle() {
      return this.$t('{something} Details', {something: this.$t('banks.singular')})
    },
    addBankTranslation() {
      return this.$t('Add {something}', {something:  this.$t('banks.plural')})
    },
    noBankAccountTranslation() {
      return this.$t('bank_accounts.there_is_no_bank_account')
    }
  },
  methods: {
    addEmptyBankAccount() {
      this.$emit('input', [
          ...this.value,
        {
          name: null,
          account_number: null,
          bank_id: null,
        },
      ])
    },
    undeletedItems() {
      return (this.value || []).filter(
          (bankAccount) => bankAccount._action !== "delete"
      );
    },
    deleteBankAt(index) {
      const items = this.undeletedItems();
      if ((items[index] || {}).id) {
        items[index]._action = "delete";
      } else {
        items.splice(index, 1);
      }
      this.$emit('input', items);
    },
    handleBankSelected(event) {
      this.$set(this.value, "bank", event);
    },
  },
};
</script>

<style scoped>
::v-deep {
  .card-title {
    font: 600 16px/24px Lato, sans-serif;
  }
}
.empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 24px 20px;
  border: 2px dashed #ccc;
  border-radius: 8px;
}

.empty-content {
  color: #98A2B3;
  font: 600 16px/24px "Inter", sans-serif;
  text-align: center;
}

.button-add-bank {
  transition: background .2s ease-in;
  border: 1px solid #D0D5DD;
  border-radius: 8px;
  font-weight: 600;
}

.button-add-bank:hover {
  background: #EEF3F7;
}
</style>
